import { Network } from 'near-safe';
import { http, Hash, TransactionRequest, createPublicClient } from 'viem';

export const getRpcUrlForChain = (chainId: number): string | null => {
  return Network.fromChainId(chainId).rpcUrl || null;
};

export const transactionRequestToSignRequestData = (
  txRequest: TransactionRequest,
  chainId: number,
  fromAddress: `0x${string}`,
) => ({
  chainId,
  method: 'eth_sendTransaction' as const,
  params: [
    {
      from: fromAddress,
      to: txRequest.to as `0x${string}`,
      data: txRequest.data as `0x${string}`,
      value: txRequest.value?.toString() as `0x${string}`,
    },
  ],
  metaTransactions: [],
});

export const getExplorerUrl = (chain: string, txHash: string): string => {
  const explorers: Record<string, string> = {
    near: 'https://nearblocks.io/txns',
    eth: 'https://etherscan.io/tx',
    base: 'https://basescan.org/tx',
    arb: 'https://arbiscan.io/tx',
    gnosis: 'https://gnosisscan.io/tx',
    btc: 'https://mempool.space/tx',
    sol: 'https://solscan.io/tx',
    doge: 'https://dogechain.info/tx',
    bnb: 'https://bscscan.com/tx',
    xrp: 'https://xrpscan.com/tx',
    dot: 'https://polkadot.subscan.io/extrinsic',
    avax: 'https://snowtrace.io/tx',
    tron: 'https://tronscan.org/#/transaction',
    sui: 'https://suiscan.xyz/tx',
    aptos: 'https://aptoscan.com/tx',
    polygon: 'https://polygonscan.com/tx',
    optimism: 'https://optimistic.etherscan.io/tx',
    arbitrum: 'https://arbiscan.io/tx',
  };

  return explorers[chain] + '/' + txHash;
};

/**
 * Validate if the provided object is a valid EVM transaction parameters object
 */
export const isValidEvmParams = (data: unknown): boolean => {
  return (
    typeof data === 'object' &&
    data !== null &&
    'to' in data &&
    typeof (data as any).to === 'string' &&
    (data as any).to.startsWith('0x')
  );
};

export const checkTransactionStatus = async (
  txHash: Hash,
  chainId: number,
): Promise<{ success: boolean; errorMessage?: string }> => {
  try {
    const rpcUrl = getRpcUrlForChain(chainId);
    if (!rpcUrl) return { success: true }; // If we can't determine, assume success

    // Create a viem public client
    const publicClient = createPublicClient({
      transport: http(rpcUrl),
    });

    // Wait for the transaction to be mined
    let receipt = null;
    let attempts = 0;
    const maxAttempts = 10;
    const pollingInterval = 1500; // 1.5 seconds between polls

    while (!receipt && attempts < maxAttempts) {
      receipt = await publicClient
        .getTransactionReceipt({
          hash: txHash,
        })
        .catch(() => null);

      if (!receipt) {
        attempts++;
        if (attempts < maxAttempts) {
          await new Promise((r) => setTimeout(r, pollingInterval));
        }
      }
    }

    // If receipt is still null after max attempts, return pending state
    if (!receipt) {
      return {
        success: true,
        errorMessage: 'Transaction pending - mining in progress',
      };
    }

    // Get additional transaction details if available
    const txDetails = await publicClient
      .getTransaction({
        hash: txHash,
      })
      .catch(() => null);

    // Get revert reason if transaction failed
    let errorMessage: string | undefined;
    if (receipt.status === 'reverted') {
      try {
        // Check if we need to simulate to get error details
        if (txDetails) {
          const { from, to, value, gas } = txDetails;
          // Transaction data might not exist on all transaction types
          const data =
            'data' in txDetails && txDetails.data
              ? (txDetails.data as Hash)
              : undefined;

          const simulationResult = await publicClient
            .call({
              account: from,
              to: to,
              value,
              data,
              gas,
            })
            .catch((err) => ({ error: err }));

          if ('error' in simulationResult) {
            errorMessage =
              simulationResult.error?.message || 'Transaction reverted';
            // Remove viem version information from error message
            if (errorMessage) {
              errorMessage = errorMessage.replace(
                /\s*Version:\s*viem@\d+\.\d+\.\d+.*$/,
                '',
              );
            }
          }
        }
      } catch (simError) {
        errorMessage = 'Transaction reverted';
      }

      if (!errorMessage) {
        errorMessage = 'Transaction reverted';
      }
    }

    return {
      success: receipt.status === 'success',
      errorMessage,
    };
  } catch (error) {
    console.error('Error checking transaction status:', error);
    // Sanitize error message if it's a string
    let sanitizedError = error instanceof Error ? error.message : String(error);
    sanitizedError = sanitizedError.replace(
      /\s*Version:\s*viem@\d+\.\d+\.\d+.*$/,
      '',
    );

    return { success: true, errorMessage: sanitizedError }; // If check fails, assume success but provide error details
  }
};

export function ensureValidHash(hash: any): Hash {
  if (!hash.startsWith('0x')) {
    return `0x${hash}` as Hash;
  }
  return hash as Hash;
}
